import type { StatusColorName } from "@kontextwerk/web-sdk/core/display/status"
import type { AppointmentPlanningRouteAnchor } from "../../../../../../api/kehrblatt/planning"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../_shared/_logic/rich-tooltips"
import type { PlanningPreviewRouteAnchorTag } from "../_shared/_logic/table-contract"

const dateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
})

const timeFormatter = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
})

type RouteAnchorRole = "arrival" | "departure"

const distanceLabel = (value?: number | null): string | null =>
  value !== null && value !== undefined && Number.isFinite(value)
    ? `${value.toFixed(1).replace(".", ",")} km`
    : null

const shortRouteAddressLabel = (value?: string | null): string | null => {
  const label = String(value ?? "").trim()

  if (!label) return null

  return label.split(",")[0]?.trim() || label
}

const routeAnchorRoleLabel = (role: RouteAnchorRole) => (role === "arrival" ? "Anfahrt" : "Abfahrt")

const routeAnchorTimePrefix = (role: RouteAnchorRole) => (role === "arrival" ? "Vorher" : "Danach")

const routeAnchorTimeRange = (anchor: AppointmentPlanningRouteAnchor): string | null => {
  if (!anchor.start_at || !anchor.end_at) return null

  const start = new Date(anchor.start_at)
  const end = new Date(anchor.end_at)

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null

  return `${timeFormatter.format(start)} bis ${timeFormatter.format(end)} Uhr`
}

const routeAnchorTooltipTimeRange = (anchor: AppointmentPlanningRouteAnchor): string | null => {
  if (!anchor.start_at || !anchor.end_at) return null

  const start = new Date(anchor.start_at)
  const end = new Date(anchor.end_at)

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null

  return `${dateTimeFormatter.format(start)} bis ${timeFormatter.format(end)} Uhr`
}

const routeAnchorKind = (anchor: AppointmentPlanningRouteAnchor) =>
  anchor.anchor_kind ?? "appointment"

const routeAnchorContextLabel = (anchor: AppointmentPlanningRouteAnchor): string => {
  const title = String(anchor.title ?? "").trim()
  switch (routeAnchorKind(anchor)) {
    case "external_block":
      return title || "Externer Blocker"
    case "absence":
      return title || "Abwesenheit"
    case "internal_block":
      return shortRouteAddressLabel(anchor.address_label) || title || "Interner Blocker"
    case "office_start":
    case "office_end":
      return shortRouteAddressLabel(anchor.address_label) || "Büro"
    case "appointment":
    default:
      return shortRouteAddressLabel(anchor.address_label) || "Liegenschaft"
  }
}

const routeAnchorSubLabel = (
  anchor: AppointmentPlanningRouteAnchor,
  role: RouteAnchorRole,
): string | null => {
  switch (routeAnchorKind(anchor)) {
    case "office_start":
      return "Start beim Büro"
    case "office_end":
      return "Ende beim Büro"
    default: {
      const timeRange = routeAnchorTimeRange(anchor)

      return timeRange ? `${routeAnchorTimePrefix(role)} ${timeRange}` : null
    }
  }
}

const estimatedTravelMetricLabel = (anchor: AppointmentPlanningRouteAnchor): string | null => {
  const minutes = anchor.estimated_travel_minutes

  if (
    !anchor.travel_time_estimated ||
    minutes === null ||
    minutes === undefined ||
    !Number.isFinite(minutes)
  ) {
    return null
  }

  return `~${Math.round(minutes)} Min.`
}

const routeAnchorMetricLabel = (anchor: AppointmentPlanningRouteAnchor): string | null =>
  estimatedTravelMetricLabel(anchor) ?? distanceLabel(anchor.distance_km)

const routeAnchorMetricAriaLabel = (
  anchor: AppointmentPlanningRouteAnchor,
  role: RouteAnchorRole,
): string | null => {
  const estimated = estimatedTravelMetricLabel(anchor)

  if (estimated) return `${routeAnchorRoleLabel(role)} geschätzt ${estimated}`

  const distance = distanceLabel(anchor.distance_km)

  return distance ? `Entfernung ${distance}` : null
}

const routeAnchorColorForKind = (anchor: AppointmentPlanningRouteAnchor): StatusColorName => {
  if (anchor.travel_time_estimated) return "amber"

  return "neutral"
}

export const buildRouteAnchorTag = (
  anchor: AppointmentPlanningRouteAnchor | null | undefined,
  role: RouteAnchorRole,
): PlanningPreviewRouteAnchorTag | null => {
  if (!anchor) return null

  const roleLabel = routeAnchorRoleLabel(role)
  const addressLabel = anchor.address_label || "Liegenschaft"
  const metric = routeAnchorMetricLabel(anchor)
  const timeRange = routeAnchorTooltipTimeRange(anchor)
  const isEstimated = Boolean(anchor.travel_time_estimated && metric)

  return {
    label: routeAnchorContextLabel(anchor),
    subLabel: routeAnchorSubLabel(anchor, role),
    metricLabel: metric,
    metricAriaLabel: routeAnchorMetricAriaLabel(anchor, role),
    color: routeAnchorColorForKind(anchor),
    tooltip: buildRichTooltip([
      tooltipTitle(roleLabel),
      timeRange ? tooltipField("Zeitraum", timeRange) : null,
      tooltipField("Adresse", addressLabel),
      anchor.title && routeAnchorKind(anchor) === "appointment"
        ? tooltipField("Auftrag", anchor.title)
        : null,
      anchor.title && routeAnchorKind(anchor) !== "appointment"
        ? tooltipField("Kontext", anchor.title)
        : null,
      metric && !isEstimated ? tooltipField("Entfernung", metric) : null,
      metric && isEstimated ? tooltipField(roleLabel, metric) : null,
      isEstimated
        ? tooltipText("Pauschal 10 Minuten Fahrtzeit, da genaue Ortsangaben fehlen.")
        : null,
    ]),
    tooltipHtml: true,
  }
}
